import { observer } from 'mobx-react-lite'
import { useRouter } from '../../hooks/router'
import { Button } from '@/components/ui/button'
import TemplateCollection from '@repo/datalayer/store/models/TemplateCollection'
import { useTranslation } from 'react-i18next'
import { Play, FileText } from 'lucide-react'

const TEMPLATE_ACCENT = 'bg-toast'

export const RecentTemplates = observer(function RecentTemplates({
  templates,
  onStart,
}: {
  templates: TemplateCollection
  onStart?: (id: string) => void
}) {
  const router = useRouter()
  const { t } = useTranslation()
  if (!templates.isReady) return null
  if (templates.items.length === 0) return null

  return (
    <div data-test-id="recent-templates-section" className="mb-10">
      <div className="flex items-center gap-3 mb-5">
        <div className="p-2 rounded-xl bg-toast/20">
          <FileText className="w-5 h-5 text-forest" />
        </div>
        <h2 className="text-xl font-bold">{t('home.recentTemplates')}</h2>
      </div>
      <div
        className={`grid gap-4 ${templates.items.length > 1 ? 'sm:grid-cols-2' : ''}`}
      >
        {templates.items.map((template) => {
          const itemCount = template.items?.length || 0

          return (
            <div
              className="group relative bg-card rounded-2xl overflow-hidden cursor-pointer shadow-card transition-all duration-300 hover:shadow-xl hover:-translate-y-1"
              data-test-id="template-card"
              data-template-name={template.name}
              key={template.id}
              onClick={() => {
                router.navigateToTemplate(template.id)
              }}
            >
              {/* Accent bar */}
              <div className={`h-1.5 ${TEMPLATE_ACCENT}`} />

              <div className="p-5">
                <div className="flex items-start gap-4">
                  <div
                    className={`p-3 rounded-xl ${TEMPLATE_ACCENT} text-forest flex-shrink-0`}
                  >
                    <FileText className="w-5 h-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-lg leading-tight truncate mb-1">
                      {template.name}
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      {itemCount} {itemCount === 1 ? 'item' : 'items'}
                    </p>
                  </div>
                  <Button
                    size="sm"
                    data-test-id="start-checklist-button"
                    className={`bg-toast-deep hover:bg-toast-deep/90 border-0 text-forest shadow-md hover:shadow-lg transition-shadow flex-shrink-0`}
                    onClick={(e) => {
                      e.stopPropagation()
                      e.preventDefault()
                      onStart?.(template.id)
                    }}
                  >
                    <Play size={14} className="mr-1.5" />
                    Start Checklist
                  </Button>
                </div>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
})
